import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
/* xlsx-js-style, not plain xlsx: the community SheetJS build silently drops
   every cell style, which is why the exported workbook came out as flat
   black-on-white. Same API, same version. */
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { REPORT_CELL_COLORS, readableOn } from "./badgeColors";
import { durationHHMM } from "./common";

/* =====================================================================
   Exporting the report document
   ---------------------------------------------------------------------
   These take the SAME line list the viewer renders — group headings, data
   rows, sub-totals and the grand total, already paginated and grouped —
   rather than the raw rows. That is the whole point: the file matches the
   document on screen, including which columns are switched on and how it
   is grouped. Handing the page's own exporter a different set of rows and
   columns is what made "Export" produce something nobody recognised.
   ===================================================================== */

const fmt = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

const fmtNumber = (n) =>
  Number.isInteger(n) ? n.toLocaleString("en-IN") : Number(n || 0).toFixed(2);

/* Duration columns hold MINUTES so they can be summed, and read as HH:MM. */
const displayCell = (col, raw) =>
  col.kind === "duration" ? durationHHMM(raw) : raw;

const displayTotal = (col, sum) =>
  col.kind === "duration" ? durationHHMM(sum) : fmtNumber(sum);

/* Excel gets the duration as a fraction of a day under a [h]:mm format,
   not as the text "01:53". That way the cell READS as 01:53 and still adds
   up — writing it as text would show the right thing and total to zero,
   which is the worst of both. [h] rather than h so a week's work shows as
   38:35 instead of wrapping to 14:35. */
const EXCEL_DURATION_FMT = "[h]:mm";
const asExcelDuration = (minutes) => (Number(minutes) || 0) / 1440;

const fmtDate = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

/* ------------------------------------------------------------------ */
/* Styling helpers for the workbook                                     */
/* ------------------------------------------------------------------ */

/* Excel wants RRGGBB with no hash. Anything that is not a plain hex colour
   — "transparent", "inherit", an unresolved hsl() — means "leave this cell
   alone", which is what the tint functions return for the values that are
   not worth marking. */
const rgb = (css) => {
  if (!css || typeof css !== "string") return null;
  const hex = css.trim().replace(/^#/, "");
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
  return hex.toUpperCase();
};

const GREY = "D9DEE7";

const border = (sides = ["top", "bottom", "left", "right"], color = "E6E9EF") =>
  sides.reduce((b, s) => ({ ...b, [s]: { style: "thin", color: { rgb: color } } }), {});

/* The tint a value earns in the report, translated for Excel. Read from the
   same REPORT_CELL_COLORS the on-screen document uses, so a Complete cell is
   the same green in the browser, on paper and in the workbook — rather than
   three colour schemes that drift apart. */
const cellTintFor = (col, value, row) => {
  const fn = REPORT_CELL_COLORS[col.key];
  if (!fn) return null;

  const colors = readableOn(fn(value, row));
  const bg = rgb(colors?.bg);
  if (!bg) return null;

  return {
    fill: { patternType: "solid", fgColor: { rgb: bg } },
    font: { color: { rgb: rgb(colors?.text) || "1A1D20" }, sz: 9 },
  };
};

const rangeText = (dateRange) =>
  dateRange?.startDate && dateRange?.endDate
    ? `From : ${fmtDate(dateRange.startDate)}   To : ${fmtDate(dateRange.endDate)}`
    : "";

/* The label a totals line carries, put in the first non-total column so it
   reads as a sentence rather than floating in column one. */
const totalsCells = (line, columns, label) => {
  const out = [`${label} (${line.count})`];
  columns.forEach((c, i) => {
    if (c.total) out.push(displayTotal(c, line.totals[c.key]));
    else out.push(i === 0 ? "" : "");
  });
  return out;
};

/* ------------------------------------------------------------------ */
/* PDF                                                                 */
/* ------------------------------------------------------------------ */
export const exportReportPdf = ({
  fileName,
  company,
  title,
  subtitle,
  dateRange,
  columns,
  lines,
  cellValue,
  paper,
  groupLabel,
}) => {
  const landscape = paper.w > paper.h;
  const doc = new jsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "mm",
    /* The sheet the viewer actually laid out, not a standard size — a wide
       report widens its own paper, and printing that onto A4 would cut the
       right-hand columns off. */
    format: [paper.w, paper.h],
  });

  const head = [["S.No", ...columns.map((c) => c.label)]];
  const totalCols = columns.length + 1;

  const body = lines.map((line) => {
    if (line.kind === "group") {
      return [
        {
          content: `${groupLabel} : ${line.label}`,
          colSpan: totalCols,
          styles: { fontStyle: "bold", fillColor: [242, 244, 247] },
        },
      ];
    }
    if (line.kind === "subtotal") {
      return totalsCells(line, columns, "Sub Total").map((v, i) => ({
        content: v,
        styles: {
          fontStyle: "bold",
          fillColor: [250, 251, 252],
          halign: i > 0 && columns[i - 1]?.total ? "right" : "left",
        },
      }));
    }
    if (line.kind === "grand") {
      return totalsCells(line, columns, "GRAND TOTAL").map((v, i) => ({
        content: v,
        styles: {
          fontStyle: "bold",
          fillColor: [238, 241, 248],
          halign: i > 0 && columns[i - 1]?.total ? "right" : "left",
        },
      }));
    }
    return [
      line.serial,
      ...columns.map((c) => fmt(displayCell(c, cellValue(c, line.row)))),
    ];
  });

  /* Column widths carried over as proportions of the printable area, so the
     PDF keeps the shape of the document rather than letting autoTable size
     everything by content. */
  const printable = paper.w - 20;
  const hints = [26, ...columns.map((c) => parseFloat(c.width) || 60)];
  const totalHint = hints.reduce((s, w) => s + w, 0);
  const columnStyles = {};
  hints.forEach((w, i) => {
    columnStyles[i] = { cellWidth: (w / totalHint) * printable };
    if (i > 0 && (columns[i - 1].total || columns[i - 1].numeric)) {
      columnStyles[i].halign = "right";
    }
  });

  const printedAt = new Date().toLocaleString("en-GB");

  autoTable(doc, {
    head,
    body,
    startY: 30,
    margin: { top: 30, left: 10, right: 10, bottom: 12 },
    theme: "grid",
    styles: {
      fontSize: columns.length > 15 ? 5.5 : 7,
      cellPadding: 1.2,
      overflow: "linebreak",
      lineColor: [225, 225, 225],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [17, 17, 17],
      fontStyle: "bold",
      lineWidth: { bottom: 0.4 },
      lineColor: [68, 68, 68],
    },
    columnStyles,
    didDrawPage: () => {
      /* Letterhead, redrawn on every sheet so page 4 is still identifiable
         once it is out of the printer and off the stack. */
      doc.setFontSize(6.5);
      doc.setTextColor(138);
      doc.text(`Report Printed : ${printedAt}`, 10, 8);

      doc.setFontSize(13);
      doc.setTextColor(26, 122, 60);
      doc.text(company, paper.w / 2, 15, { align: "center" });

      doc.setFontSize(9);
      doc.setTextColor(160, 27, 27);
      doc.text(
        subtitle ? `${title} — ${subtitle}` : title,
        paper.w / 2,
        20,
        { align: "center" },
      );

      const range = rangeText(dateRange);
      if (range) {
        doc.setFontSize(7);
        doc.setTextColor(28, 63, 170);
        doc.text(range, paper.w / 2, 24.5, { align: "center" });
      }

      doc.setDrawColor(28, 63, 170);
      doc.setLineWidth(0.5);
      doc.line(10, 26.5, paper.w - 10, 26.5);

      doc.setFontSize(6.5);
      doc.setTextColor(138);
      const page = doc.internal.getCurrentPageInfo().pageNumber;
      doc.text(company, 10, paper.h - 5);
      doc.text(`Page ${page}`, paper.w - 10, paper.h - 5, { align: "right" });
    },
  });

  doc.save(`${fileName}.pdf`);
};

/* ------------------------------------------------------------------ */
/* Excel — .xlsx and the older .xls                                    */
/* ------------------------------------------------------------------ */
export const exportReportSheet = ({
  fileName,
  company,
  title,
  subtitle,
  dateRange,
  columns,
  lines,
  cellValue,
  groupLabel,
  bookType = "xlsx",
}) => {
  const aoa = [];

  aoa.push([company]);
  aoa.push([subtitle ? `${title} — ${subtitle}` : title]);
  const range = rangeText(dateRange);
  if (range) aoa.push([range]);
  aoa.push([`Report Printed : ${new Date().toLocaleString("en-GB")}`]);
  aoa.push([]);

  const headerRow = aoa.length; // 0-based index of the column headings
  aoa.push(["S.No", ...columns.map((c) => c.label)]);

  lines.forEach((line) => {
    if (line.kind === "group") {
      aoa.push([`${groupLabel} : ${line.label}`]);
      return;
    }
    if (line.kind === "subtotal" || line.kind === "grand") {
      const label = line.kind === "grand" ? "GRAND TOTAL" : "Sub Total";
      const row = [`${label} (${line.count})`];
      columns.forEach((c) => {
        /* Numbers stay numbers so the totals can be re-summed in Excel —
           writing "1,234" as text is what makes an exported report useless
           for anything except looking at. */
        if (!c.total) {
          row.push("");
        } else if (c.kind === "duration") {
          row.push(asExcelDuration(line.totals[c.key]));
        } else {
          row.push(Number(line.totals[c.key] ?? 0));
        }
      });
      aoa.push(row);
      return;
    }
    aoa.push([
      line.serial,
      ...columns.map((c) => {
        const v = cellValue(c, line.row);
        if (c.kind === "duration") return asExcelDuration(v);
        if (c.total || c.numeric) {
          const n = Number(v);
          return Number.isFinite(n) ? n : fmt(v);
        }
        return fmt(v);
      }),
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const lastCol = columns.length;

  const style = (r, c, s) => {
    /* aoa_to_sheet creates no cell for an empty string, so a fill on a blank
       cell has nothing to attach to — the totals rows have plenty of those,
       and without this the tint stopped halfway across the row. */
    const ref = XLSX.utils.encode_cell({ r, c });
    if (!ws[ref]) ws[ref] = { t: "s", v: "" };
    ws[ref].s = { ...(ws[ref].s || {}), ...s };
  };

  const rowStyle = (r, s) => {
    for (let c = 0; c <= lastCol; c += 1) style(r, c, s);
  };

  /* The number format lives on the cell, not in its style block. Setting it
     via `z` is what makes Excel render 0.0785 as 01:53 while still holding a
     number underneath. */
  const setDurationFormat = (r, c) => {
    const ref = XLSX.utils.encode_cell({ r, c });
    if (ws[ref] && typeof ws[ref].v === "number") {
      ws[ref].t = "n";
      ws[ref].z = EXCEL_DURATION_FMT;
    }
  };

  /* --- letterhead ------------------------------------------------- */
  style(0, 0, { font: { bold: true, sz: 14, color: { rgb: "1A7A3C" } } });
  style(1, 0, { font: { bold: true, sz: 11, color: { rgb: "A01B1B" } } });
  if (range) style(2, 0, { font: { bold: true, sz: 9, color: { rgb: "1C3FAA" } } });
  style(headerRow - 1, 0, { font: { sz: 8, color: { rgb: "8A8A8A" } } });

  // Merged across the table so the title is not clipped by column A's width.
  ws["!merges"] = [];
  for (let r = 0; r < headerRow - 1; r += 1) {
    ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: lastCol } });
  }

  /* --- column headings --------------------------------------------- */
  rowStyle(headerRow, {
    font: { bold: true, sz: 9, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "4F46E5" } },
    alignment: { wrapText: true, vertical: "center" },
    border: border(["top", "bottom", "left", "right"], "3F37C9"),
  });

  /* --- body --------------------------------------------------------- */
  let r = headerRow;
  lines.forEach((line) => {
    r += 1;

    if (line.kind === "group") {
      ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: lastCol } });
      rowStyle(r, {
        font: { bold: true, sz: 10, color: { rgb: "1F2937" } },
        fill: { patternType: "solid", fgColor: { rgb: "EDF0F5" } },
        border: border(["top", "bottom"], GREY),
      });
      return;
    }

    if (line.kind === "subtotal" || line.kind === "grand") {
      const grand = line.kind === "grand";
      rowStyle(r, {
        font: { bold: true, sz: 9, color: { rgb: grand ? "10275C" : "1F2937" } },
        fill: {
          patternType: "solid",
          fgColor: { rgb: grand ? "DDE4F5" : "F2F5F9" },
        },
        border: border(["top", "bottom"], grand ? "1C3FAA" : GREY),
        alignment: { horizontal: "right" },
      });
      style(r, 0, { alignment: { horizontal: "left" } });
      // Sub-totals of a duration are durations too.
      columns.forEach((c, i) => {
        if (c.kind === "duration") setDurationFormat(r, i + 1);
      });
      return;
    }

    /* Data row: the base look, then the tint each value has earned. This is
       the "important data" — Status, Priority, Team, Query Type, Is Update
       and the Yes flags all carry the colour they have on screen, so the
       workbook can be scanned the same way. */
    rowStyle(r, {
      font: { sz: 9 },
      alignment: { wrapText: true, vertical: "top" },
      border: border(["bottom"], "EEF0F4"),
    });
    style(r, 0, { alignment: { horizontal: "center", vertical: "top" } });

    columns.forEach((c, i) => {
      const value = cellValue(c, line.row);
      if (c.total || c.numeric) {
        style(r, i + 1, { alignment: { horizontal: "right", vertical: "top" } });
      }
      if (c.kind === "duration") setDurationFormat(r, i + 1);
      const tint = cellTintFor(c, value, line.row);
      if (tint) {
        style(r, i + 1, {
          ...tint,
          font: { ...tint.font, bold: true },
          alignment: { horizontal: "center", vertical: "center" },
        });
      }
    });
  });

  // Column widths from the same hints the document lays out with.
  ws["!cols"] = [
    { wch: 6 },
    ...columns.map((c) => ({
      wch: Math.min(60, Math.max(8, Math.round((parseFloat(c.width) || 60) / 6))),
    })),
  ];

  // Taller heading row, since the labels now wrap inside it.
  ws["!rows"] = [];
  ws["!rows"][headerRow] = { hpt: 26 };

  // Freeze the heading row so the columns stay visible while scrolling.
  ws["!freeze"] = { xSplit: 0, ySplit: headerRow + 1 };
  ws["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRow, c: 0 },
      e: { r: headerRow, c: lastCol },
    }),
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");

  const ext = bookType === "biff8" ? "xls" : "xlsx";
  const out = XLSX.write(wb, { bookType, type: "array" });
  saveAs(new Blob([out], { type: "application/octet-stream" }), `${fileName}.${ext}`);
};

/* ------------------------------------------------------------------ */
/* Sectioned documents — several tables on one sheet                   */
/* ------------------------------------------------------------------ */
/* The Daily Management Report is three tables with nothing in common but the
   paper they print on. The exporters above take ONE column set and one line
   list, so pointing them at a sectioned report handed back whatever the
   viewer's single-table props happened to hold — which on the Support Team
   Report is the User Query detail. The right report was on screen and the
   wrong one came out of the button.
   These walk the sections instead. */

const sectionBody = (sec) =>
  (sec.rows || []).map((r, i) => [
    i + 1,
    ...(sec.columns || []).map((c) =>
      fmt(c.value ? c.value(r) : r[c.key]),
    ),
  ]);

export const exportSectionsPdf = ({
  fileName,
  company,
  title,
  subtitle,
  dateRange,
  sections,
  paper,
}) => {
  const landscape = paper.w > paper.h;
  const doc = new jsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "mm",
    format: [paper.w, paper.h],
  });

  const printedAt = new Date().toLocaleString("en-GB");

  const letterhead = () => {
    doc.setFontSize(6.5);
    doc.setTextColor(138);
    doc.text(`Report Printed : ${printedAt}`, 10, 8);

    doc.setFontSize(13);
    doc.setTextColor(26, 122, 60);
    doc.text(company, paper.w / 2, 15, { align: "center" });

    doc.setFontSize(9);
    doc.setTextColor(160, 27, 27);
    doc.text(subtitle ? `${title} — ${subtitle}` : title, paper.w / 2, 20, {
      align: "center",
    });

    const range = rangeText(dateRange);
    if (range) {
      doc.setFontSize(7);
      doc.setTextColor(28, 63, 170);
      doc.text(range, paper.w / 2, 24.5, { align: "center" });
    }

    doc.setDrawColor(28, 63, 170);
    doc.setLineWidth(0.5);
    doc.line(10, 26.5, paper.w - 10, 26.5);

    doc.setFontSize(6.5);
    doc.setTextColor(138);
    doc.text(company, 10, paper.h - 5);
    doc.text(
      `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
      paper.w - 10,
      paper.h - 5,
      { align: "right" },
    );
  };

  sections.forEach((sec, i) => {
    const cols = sec.columns || [];
    const span = cols.length + 1;

    /* The section's name rides in the head, so it repeats with the column
       labels when a long table runs onto the next sheet. */
    const head = [
      [
        {
          content: sec.title,
          colSpan: span,
          styles: {
            halign: "left",
            fillColor: [238, 242, 255],
            textColor: [31, 41, 55],
            fontStyle: "bold",
            fontSize: 8,
          },
        },
      ],
      ["S.No", ...cols.map((c) => c.label)],
    ];

    const printable = paper.w - 20;
    const hints = [22, ...cols.map((c) => parseFloat(c.width) || 60)];
    const totalHint = hints.reduce((n, w) => n + w, 0);
    const columnStyles = {};
    hints.forEach((w, ci) => {
      columnStyles[ci] = { cellWidth: (w / totalHint) * printable };
      if (ci > 0 && cols[ci - 1].numeric) columnStyles[ci].halign = "right";
    });

    autoTable(doc, {
      head,
      body: sectionBody(sec).length
        ? sectionBody(sec)
        : [[{ content: "Nothing to report in this section.", colSpan: span }]],
      startY: i === 0 ? 30 : (doc.lastAutoTable?.finalY ?? 30) + 7,
      margin: { top: 30, left: 10, right: 10, bottom: 12 },
      theme: "grid",
      styles: {
        fontSize: cols.length > 10 ? 5.5 : 7,
        cellPadding: 1.2,
        overflow: "linebreak",
        lineColor: [225, 225, 225],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [17, 17, 17],
        fontStyle: "bold",
        lineWidth: { bottom: 0.4 },
        lineColor: [68, 68, 68],
      },
      columnStyles,
      didDrawPage: letterhead,
    });
  });

  doc.save(`${fileName}.pdf`);
};

export const exportSectionsSheet = ({
  fileName,
  company,
  title,
  subtitle,
  dateRange,
  sections,
  bookType = "xlsx",
}) => {
  const aoa = [];
  const titleRows = [];
  const headRows = [];

  aoa.push([company]);
  aoa.push([subtitle ? `${title} — ${subtitle}` : title]);
  const range = rangeText(dateRange);
  if (range) aoa.push([range]);
  aoa.push([`Report Printed : ${new Date().toLocaleString("en-GB")}`]);
  aoa.push([]);

  let widest = 1;

  sections.forEach((sec, i) => {
    if (i > 0) aoa.push([]);

    titleRows.push(aoa.length);
    aoa.push([sec.title]);

    const cols = sec.columns || [];
    widest = Math.max(widest, cols.length + 1);

    headRows.push(aoa.length);
    aoa.push(["S.No", ...cols.map((c) => c.label)]);

    const body = sectionBody(sec);
    if (!body.length) {
      aoa.push(["", "Nothing to report in this section."]);
      return;
    }
    body.forEach((row) => aoa.push(row));
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  const at = (r, c) => XLSX.utils.encode_cell({ r, c });

  /* Document titles at the top. */
  [0, 1].forEach((r, i) => {
    const cell = ws[at(r, 0)];
    if (cell) {
      cell.s = {
        font: { bold: true, sz: i === 0 ? 14 : 11, color: { rgb: i === 0 ? "1A7A3C" : "A01B1B" } },
        alignment: { horizontal: "left" },
      };
    }
  });

  titleRows.forEach((r) => {
    const cell = ws[at(r, 0)];
    if (cell) {
      cell.s = {
        font: { bold: true, sz: 11, color: { rgb: "1F2937" } },
        fill: { fgColor: { rgb: "EEF2FF" } },
      };
    }
  });

  headRows.forEach((r) => {
    for (let c = 0; c < widest; c += 1) {
      const cell = ws[at(r, c)];
      if (!cell) continue;
      cell.s = {
        font: { bold: true, color: { rgb: "111111" } },
        fill: { fgColor: { rgb: "F8FAFC" } },
        alignment: { horizontal: "left", wrapText: true, vertical: "center" },
        border: { bottom: { style: "medium", color: { rgb: "444444" } } },
      };
    }
  });

  ws["!cols"] = Array.from({ length: widest }, (_, i) =>
    i === 0 ? { wch: 6 } : { wch: 22 },
  );

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");

  const ext = bookType === "biff8" || bookType === "xls" ? "xls" : "xlsx";
  const out = XLSX.write(wb, { bookType, type: "array" });
  saveAs(new Blob([out], { type: "application/octet-stream" }), `${fileName}.${ext}`);
};
